import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { eErroDaApi, type ErroDaApi } from '@/lib/api/erros';

/** Metadados aceitos em consultas e alterações. */
export interface MetaDoPainel extends Record<string, unknown> {
  /** O 401 é esperado e tratado pela tela (ex. senha errada ao confirmar a identidade). */
  ignorar401?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: MetaDoPainel;
    mutationMeta: MetaDoPainel;
  }
}

/**
 * Ganchos do tratamento central de erros. A spec 002 liga o 401 ao fluxo de sessão expirada; a
 * spec 003 liga o 403 à releitura do `/me` quando o motivo muda o estado da sessão.
 */
export interface TratamentoDeErros {
  aoNaoAutenticado?: (erro: unknown) => void;
  aoSemPermissao?: (erro: ErroDaApi) => void;
}

/**
 * Consultas tentam mais uma vez só quando o erro pode passar sozinho (rede ou 5xx); 4xx é
 * resposta definitiva do servidor (ADR-0005, CA-07).
 */
export function deveTentarDeNovo(falhas: number, erro: unknown): boolean {
  if (falhas >= 1 || !eErroDaApi(erro)) return false;
  return erro.semResposta || erro.status >= 500;
}

export function criarQueryClient(tratamento: TratamentoDeErros = {}): QueryClient {
  const aoErrar = (erro: unknown, meta: MetaDoPainel | undefined) => {
    if (meta?.ignorar401 === true) return;
    if (eErroDaApi(erro) && erro.status === 401) tratamento.aoNaoAutenticado?.(erro);
    if (eErroDaApi(erro) && erro.status === 403) tratamento.aoSemPermissao?.(erro);
  };

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (erro, consulta) => {
        aoErrar(erro, consulta.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (erro, _variaveis, _contexto, mutacao) => {
        aoErrar(erro, mutacao.meta);
      },
    }),
    defaultOptions: {
      queries: { retry: deveTentarDeNovo, staleTime: 30_000 },
      // Alteração nunca repete sozinha: quem decide tentar de novo é a pessoa (CA-07).
      mutations: { retry: false },
    },
  });
}
